<script setup>
import { onMounted, onBeforeUnmount, ref } from 'vue'
import { useI18n } from 'vue-i18n'

// Cloudflare Turnstile — an invisible bot check on the two public POSTs that
// create durable work. Renders NOTHING when VITE_TURNSTILE_SITE_KEY is blank,
// which is the shipped-dark default (same treatment ChatwootWidget.vue gives
// its own build-time variable). The site key is public by design; the secret
// lives only on the server.
//
// `refresh-expired: auto` is load-bearing, not a nicety: a Turnstile token is
// single-use and dies after 300 seconds, and the checkout form is long enough
// that a customer filling it in slowly would otherwise submit a dead token and
// be rejected for nothing.
defineProps({ modelValue: { type: String, default: '' } })
const emit = defineEmits([ 'update:modelValue' ])
const { t } = useI18n()

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
// If challenges.cloudflare.com is blocked outright (ad-blocker, privacy
// extension, VPN, corporate firewall — realistic for our Russian/Chinese
// visitors), some setups fire neither `load` nor `error` on the <script> tag,
// so a hard timeout is the only real backstop.
const SCRIPT_TIMEOUT_MS = 10000
const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY

const host = ref(null)
// True once we've established the script never gave us `window.turnstile` —
// drives a visible, localized fallback instead of silently rendering nothing
// (with a real Turnstile secret configured server-side, a blank token is
// correctly rejected fail-closed, so a stuck widget would otherwise lock the
// customer out of checkout/request-to-book with no explanation).
const failed = ref(false)
let widgetId = null

// Always settles — never rejects, never hangs. A script tag stuck "loading"
// forever would hang onMounted forever, and because it persists in
// document.head across Inertia SPA navigations, every later TurnstileWidget
// mount on the same page load would reuse the same dead tag and hang too.
function loadScript() {
  if (window.turnstile) return Promise.resolve()
  const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`)
  const target = existing || document.createElement('script')
  return new Promise((resolve) => {
    let settled = false
    let timer
    const done = () => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      resolve()
    }
    timer = setTimeout(done, SCRIPT_TIMEOUT_MS)
    target.addEventListener('load', done, { once: true })
    target.addEventListener('error', done, { once: true })
    if (!existing) {
      target.src = SCRIPT_SRC
      target.async = true
      target.defer = true
      document.head.appendChild(target)
    }
  })
}

onMounted(async () => {
  if (!siteKey || !host.value) return
  await loadScript()
  if (!host.value) return // unmounted while the script was (still) loading
  if (!window.turnstile) {
    failed.value = true
    console.warn('[TurnstileWidget] challenges.cloudflare.com did not load in time — likely blocked by an ad blocker, VPN, or firewall.')
    return
  }
  widgetId = window.turnstile.render(host.value, {
    sitekey: siteKey,
    'refresh-expired': 'auto',
    callback: token => emit('update:modelValue', token),
    'expired-callback': () => emit('update:modelValue', ''),
    'error-callback': () => emit('update:modelValue', '')
  })
})

onBeforeUnmount(() => {
  if (widgetId !== null && window.turnstile) window.turnstile.remove(widgetId)
  widgetId = null
})

// A consumed token cannot be replayed, so every failed submit must mint a new
// one before the customer retries.
function reset() {
  if (widgetId === null || !window.turnstile) return
  window.turnstile.reset(widgetId)
  emit('update:modelValue', '')
}

defineExpose({ reset })
</script>

<template>
  <div v-if="siteKey && !failed" ref="host" class="turnstile-widget" />
  <p v-else-if="failed" class="turnstile-fallback">{{ t('public.checkout.errors.challenge_unavailable') }}</p>
</template>

<style scoped>
.turnstile-widget { margin: 0.2rem 0; }
.turnstile-fallback { margin: 0; font-size: 0.8rem; color: var(--warn); }
</style>
